import React, { useEffect, useRef, useState } from "react";
import Text from "../../../common/typography/text.js";
import Button from "../../../common/button/button.js";
import TrendChartBody from "./TrendChartBody.js";
import { fetchTrend } from "../aabiService";
import { buildPeriodParams } from "../aabiPeriods";
import Panel from "../../../common/panel/index.js";
import { useScreen } from "../../Utils/Screens.js";
import "./../AABI.css";

// Revenue performance trend (§6.1): the selected period as a solid primary
// line with the immediately preceding period overlaid as a muted dashed line,
// aligned by bucket ordinal. Owns its own fetch so a trend failure never
// blocks KPIs or the portfolio explorer (§7.6).
const PerformanceTrendChart = ({
  manufacturerId,
  manufacturerName,
  scope,
  period,
  measure,
}) => {
  const isPhone = !useScreen("sm");
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState("revenue"); // Revenue | UOM within the panel (§6.1)
  // Phones default to the Data view (§10.3): a 300px-tall line chart is
  // unreadable at that width, and unlike the donut slots this panel has no
  // ranked list to fall back on — hiding the chart alone left an empty panel.
  const [view, setView] = useState(isPhone ? "data" : "chart");
  const viewChosenRef = useRef(false);
  const requestIdRef = useRef(0);

  // Follow the breakpoint until the user picks a view themselves; after that
  // their choice sticks across resizes.
  useEffect(() => {
    if (viewChosenRef.current) return;
    setView(isPhone ? "data" : "chart");
  }, [isPhone]);

  const chooseView = (nextView) => {
    viewChosenRef.current = true;
    setView(nextView);
  };

  const load = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const data = await fetchTrend({
        manufacturerId,
        manufacturerName,
        categoryId: scope.categoryId,
        groupId: scope.groupId,
        productId: scope.productId,
        ...buildPeriodParams({ startDate: period.startDate, endDate: period.endDate }),
        granularity: "auto", // Stage 1 UI always requests auto (§8.2)
      });
      if (requestId !== requestIdRef.current) return;
      setTrend(data);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to fetch AABI trend", err);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    manufacturerId,
    scope.categoryId,
    scope.groupId,
    scope.productId,
    period.startDate,
    period.endDate,
  ]);

  return (
    <Panel
      className="aabi-trend"
      title="Revenue performance"
      aria-label="Revenue performance"
      aria-busy={loading}
      actions={
        <>
          <Button
            variant="timeframe"
            isActive={mode === "revenue"}
            onClick={() => setMode("revenue")}
          >
            Revenue
          </Button>
          <Button
            variant="timeframe"
            isActive={mode === "uom"}
            onClick={() => setMode("uom")}
          >
            UOM
          </Button>
          <Button
            variant="timeframe"
            isActive={view === "chart"}
            onClick={() => chooseView("chart")}
          >
            Chart
          </Button>
          <Button
            variant="timeframe"
            isActive={view === "data"}
            onClick={() => chooseView("data")}
          >
            Data
          </Button>
        </>
      }
    >
      {error ? (
        <div role="alert" className="aabi-panel-error">
          <Text variant="smallText" color="failureColor">
            Couldn&apos;t load the performance trend.
          </Text>
          <Button variant="primary" onClick={load}>
            Retry
          </Button>
        </div>
      ) : loading ? (
        <div className="aabi-trend-skeleton" aria-hidden="true">
          <div className="aabi-kpi-skeleton" />
          <div className="aabi-kpi-skeleton tall" />
        </div>
      ) : !trend?.currentSeries?.length ? (
        <div className="aabi-empty-state">
          <Text variant="smallText" color="lightText">
            Not enough activity to show a trend.
          </Text>
        </div>
      ) : (
        <TrendChartBody trend={trend} measure={measure} mode={mode} view={view} />
      )}
    </Panel>
  );
};

export default PerformanceTrendChart;
